<template>
  <v-card>
    <v-card-title class="pb-0">
      指名系集計
    </v-card-title>

    <v-tabs
      v-model="activeTab"
      class="px-4"
    >
      <v-tab>集計</v-tab>
      <v-tab>タイムライン</v-tab>
    </v-tabs>

    <v-divider />

    <v-card-text>
      <!-- 集計タブ -->
      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-skeleton-loader
            v-if="isLoading"
            type="table"
          />

          <div v-else-if="hasData">
            <div style="overflow-x: auto;">
              <v-simple-table dense>
                <template #default>
                  <thead>
                    <tr>
                      <th class="sticky-left">
                        受領者
                      </th>
                      <th
                        v-for="tag in nominationStats.vendibleTags"
                        :key="tag"
                        class="text-center"
                      >
                        {{ tag }}
                      </th>
                      <th class="text-center grey lighten-3">
                        合計
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="recipient in nominationStats.recipients"
                      :key="recipient.recipientId"
                    >
                      <td class="sticky-left font-weight-medium">
                        {{ recipient.recipientName || '未指定' }}
                      </td>
                      <td
                        v-for="tag in nominationStats.vendibleTags"
                        :key="tag"
                        class="text-center"
                      >
                        {{ recipient.byTag[tag] || 0 }}
                      </td>
                      <td class="text-center grey lighten-4 font-weight-bold">
                        {{ recipient.total }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot class="grey lighten-3">
                    <tr>
                      <td class="sticky-left font-weight-bold">
                        合計
                      </td>
                      <td
                        v-for="tag in nominationStats.vendibleTags"
                        :key="tag"
                        class="text-center font-weight-bold"
                      >
                        {{ nominationStats.totalsByTag[tag] || 0 }}
                      </td>
                      <td class="text-center font-weight-bold">
                        {{ nominationStats.grandTotal }}
                      </td>
                    </tr>
                  </tfoot>
                </template>
              </v-simple-table>
            </div>
          </div>

          <div
            v-else
            class="text-center text--secondary"
          >
            ノミネーション履歴がありません
          </div>
        </v-tab-item>

        <!-- タイムラインタブ -->
        <v-tab-item>
          <v-skeleton-loader
            v-if="isLoadingTimeline"
            type="table"
          />

          <div v-else-if="timeline.length > 0">
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th>日時</th>
                    <th>受領キャスト</th>
                    <th>指名種別</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in timeline"
                    :key="item.visitId"
                  >
                    <td>{{ formatDate(item.visitedAt) }}</td>
                    <td>{{ item.recipientNames.length > 0 ? item.recipientNames.join(', ') : '-' }}</td>
                    <td>{{ item.vendibleTagNames.length > 0 ? item.vendibleTagNames.join(', ') : '-' }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <div
              v-if="timelinePagination.totalPages > 1"
              class="d-flex justify-center mt-4"
            >
              <v-pagination
                v-model="timelinePage"
                :length="timelinePagination.totalPages"
                :total-visible="5"
                @input="fetchTimeline"
              />
            </div>
          </div>

          <div
            v-else
            class="text-center text--secondary"
          >
            来店履歴がありません
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ref,
  computed,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import CustomerNominationStatsApi from '@/api/waiter/CustomerNominationStats'

export default {
  name: 'CustomerNominationStats',
  props: {
    customerId: {
      type: [Number, String],
      default: null,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy

    const activeTab = ref(0)

    // --- 集計タブ ---
    const isLoading = ref(false)
    const nominationStats = ref({
      vendibleTags: [],
      recipients: [],
      totalsByTag: {},
      grandTotal: 0,
    })

    const hasData = computed(() => {
      return nominationStats.value.recipients.length > 0
    })

    const fetchNominationStats = async () => {
      if (!props.customerId) return

      const customerIdNum = Number(props.customerId)
      if (Number.isNaN(customerIdNum) || customerIdNum <= 0) return

      isLoading.value = true
      try {
        const res = await CustomerNominationStatsApi.getCustomerNominationStats(customerIdNum)
        if (res?.data) {
          nominationStats.value = res.data
        }
      } catch (error) {
        console.error('Failed to fetch nomination stats:', error)
        if (error?.response?.status !== 404) {
          vm.$toast.error('ノミネーション集計の取得に失敗しました')
        }
      } finally {
        isLoading.value = false
      }
    }

    // --- タイムラインタブ ---
    const isLoadingTimeline = ref(false)
    const timeline = ref([])
    const timelinePage = ref(1)
    const timelinePagination = ref({
      page: 1,
      perPage: 10,
      totalCount: 0,
      totalPages: 0,
    })
    const timelineLoaded = ref(false)

    const fetchTimeline = async (page = 1) => {
      if (!props.customerId) return

      const customerIdNum = Number(props.customerId)
      if (Number.isNaN(customerIdNum) || customerIdNum <= 0) return

      isLoadingTimeline.value = true
      try {
        const res = await CustomerNominationStatsApi.getNominationTimeline(customerIdNum, {
          page,
          perPage: 10,
        })
        if (res?.data) {
          timeline.value = res.data.nominationTimeline || []
          timelinePagination.value = res.data.pagination || timelinePagination.value
        }
      } catch (error) {
        console.error('Failed to fetch nomination timeline:', error)
        if (error?.response?.status !== 404) {
          vm.$toast.error('ノミネーションタイムラインの取得に失敗しました')
        }
      } finally {
        isLoadingTimeline.value = false
      }
    }

    const formatDate = dateStr => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)

      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }

    // タブ切替時にタイムラインを遅延ロード
    watch(activeTab, newVal => {
      if (newVal === 1 && !timelineLoaded.value) {
        timelineLoaded.value = true
        fetchTimeline()
      }
    })

    watch(
      () => props.customerId,
      newVal => {
        if (newVal) {
          fetchNominationStats()
          // タイムラインタブが開いていればリロード
          if (activeTab.value === 1) {
            timelineLoaded.value = true
            fetchTimeline()
          } else {
            timelineLoaded.value = false
          }
        }
      },
      { immediate: true },
    )

    return {
      activeTab,
      isLoading,
      nominationStats,
      hasData,
      isLoadingTimeline,
      timeline,
      timelinePage,
      timelinePagination,
      formatDate,
      fetchTimeline,
    }
  },
}
</script>

<style scoped>
.sticky-left {
  position: sticky;
  left: 0;
  background-color: inherit;
  z-index: 1;
}
</style>
